import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { AuthenticatedUserService } from '@app/shared/auth/keycloak/authenticated-user-service';
import { KeycloakTokenRefresher } from '@app/shared/auth/keycloak/keycloak-token-refresher.service';
import { LogoutService } from '@app/shared/auth/keycloak/logout-service';
import { OpenUserManagementService } from '@app/shared/auth/keycloak/open-user-management.service';
import { LOGOUT } from '@app/shared/auth/logout/logout.token';
import { OPEN_USER_MANAGEMENT } from '@app/shared/auth/user-management/open-user-management.token';
import { inject, provideAppInitializer } from '@angular/core';
import { Embedding } from '@app/shared/shell/shell-protocol';
import { provideKeycloak } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { KeycloakRuntimeConfig } from '../runtime-config';
import { initEmbeddedKeycloak, standaloneInitOptions } from './embedded-keycloak';

/**
 * L'annuaire est celui que désigne `/config.json`, lu avant l'appel : rien ici ne dépend de la
 * configuration de build.
 *
 * Ouverte seule (`embedding` nul), l'application se connecte par la page de l'annuaire. Dans le
 * cadre de la coque, elle vérifie la session en silence, et sans session n'affiche rien
 * (contrat des logiciels, § 3) : l'initialisation est alors la sienne, et non celle de
 * keycloak-angular, qui démarrerait l'application sans jeton.
 *
 * see https://www.keycloak.org/securing-apps/javascript-adapter
 */
export function vanexKeycloakProvider(
  keycloak: KeycloakRuntimeConfig,
  embedding: Embedding | null = null,
) {
  const config = { url: keycloak.url, realm: keycloak.realm, clientId: keycloak.clientId };
  return [
    ...(embedding
      ? [
          provideKeycloak({ config }),
          provideAppInitializer(() =>
            initEmbeddedKeycloak(inject(Keycloak), embedding, window.location.origin),
          ),
        ]
      : [provideKeycloak({ config, initOptions: standaloneInitOptions() })]),
    // Eagerly instantiate the token refresher service so it starts refreshing tokens
    {
      provide: KeycloakTokenRefresher,
      useClass: KeycloakTokenRefresher,
    },
    { provide: LOGOUT, useClass: LogoutService },
    {
      provide: OPEN_USER_MANAGEMENT,
      useValue: new OpenUserManagementService(userManagementUrl(keycloak)),
    },
    { provide: AUTHENTICATED_USER_SERVICE, useClass: AuthenticatedUserService },
  ];
}

/** La liste des utilisateurs du realm, dans la console d'administration de l'annuaire. */
export function userManagementUrl({ url, realm }: KeycloakRuntimeConfig): string {
  return `${url}/admin/${realm}/console/#/${realm}/users`;
}
